import React from "react";
import UserApi from "@/api/users/users";
import { Option, SelectBox, TextInput } from "@monolith-forensics/monolith-ui";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { useTheme } from "styled-components";
import styled from "styled-components";

// The original hardcoded helper text. Kept as the default so the existing
// callers render unchanged.
const DEFAULT_DESCRIPTION = "Select a user assigned to this case.";

interface UserSelectorProps {
  // A users.user_id. A string form is accepted because callers seed it from
  // stringified defaults; matched against the options by value.
  defaultValue?: string | number | null;
  // Receives the selected user_id, or undefined when the box is cleared.
  onChange?: (value: number | undefined) => void;
  error?: string;
  openOnFocus?: boolean;
  label?: string;
  description?: string;
  isRequired?: boolean;
  query?: {
    case_id?: number;
    includeInactive?: boolean;
    includeObservers?: boolean;
  };
  // An option to show even when the fetched list excludes it — e.g. the
  // current assignee who is now inactive under includeInactive: false — so
  // the stored value is visible and clearable. Ignored when the list already
  // contains the same user_id.
  pinnedOption?: { value: number; label: string };
}

type User = {
  user_id: number;
  full_name: string;
  email: string;
  active: number;
  observer: number;
  title?: string;
  user_role?: {
    role_name: string;
  };
  office_name?: string;
};

// Tooltip content component
const TooltipContent = styled(
  ({ className, data }: { className?: string; data?: User }) => {
    // A pinned option carries no user record, so there is nothing to show.
    if (!data) return null;
    return (
      <div className={className}>
        <div className="detail-title">User Details</div>
        <div className="detail-item">
          <div className="detail-label">Title: </div>
          <div className="detail-value">{data.title || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Role: </div>
          <div className="detail-value">
            {data.user_role?.role_name || "None"}
          </div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Office: </div>
          <div className="detail-value">{data.office_name || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Email: </div>
          <div className="detail-value">{data.email || "None"}</div>
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 300px;

  .detail-title {
    font-weight: bold;
    font-size: 15px;
    margin-bottom: 8px;
  }

  .detail-item {
    display: flex;
    gap: 2px;
  }

  .detail-label {
    font-weight: bold;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 12px;
  }

  .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 12px;
  }
`;

const UserContent: React.FC<{ user: User }> = ({ user }) => {
  const theme = useTheme() as { palette: { text: { secondary: string } } };

  if (!user.title) return null;

  return (
    <div
      style={{
        fontSize: "0.9em",
        marginTop: "4px",
        color: theme.palette.text.secondary,
        lineHeight: "1.2",
        display: "-webkit-box",
        WebkitLineClamp: 1,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {user.title}
    </div>
  );
};

const UserSelector: FC<UserSelectorProps> = ({
  defaultValue,
  onChange,
  error,
  label,
  description = DEFAULT_DESCRIPTION,
  openOnFocus = false,
  isRequired = false,
  query,
  pinnedOption,
}) => {
  const { data: userList, isLoading } = useQuery<User[]>({
    queryKey: query ? ["users", "list", query] : ["users", "list"],
    queryFn: () =>
      UserApi.getUsers({
        ...query,
      }),
  });

  const fetchedOptions: Option[] =
    userList?.map((user) => ({
      label: user?.full_name,
      value: user?.user_id,
      data: user,
    })) || [];

  const options: Option[] =
    pinnedOption &&
    !fetchedOptions.some((option) => option.value === pinnedOption.value)
      ? [pinnedOption, ...fetchedOptions]
      : fetchedOptions;

  // Compared as strings: the value is a number but a caller may seed the
  // stringified id.
  const defaultUser =
    defaultValue === undefined || defaultValue === null
      ? undefined
      : options.find((user) => String(user.value) === String(defaultValue));

  // Show placeholder component while loading option data from api
  if (isLoading || !userList)
    return (
      <TextInput
        size="sm"
        variant="outlined"
        label={label || "User"}
        placeholder="Select user..."
        description={description}
        loading={true}
        disabled={true}
        value={defaultUser?.label}
      />
    );

  return (
    <>
      <SelectBox
        defaultValue={defaultUser}
        size="sm"
        variant="outlined"
        label={label || "User"}
        placeholder="Select user..."
        description={description}
        onChange={onChange}
        data={options}
        error={error}
        clearable={true}
        searchable={true}
        openOnFocus={openOnFocus}
        required={isRequired}
        loading={isLoading}
        OptionTooltip={TooltipContent}
        renderOption={(item: string | Option) => {
          if (typeof item === "string") {
            return <div>{item}</div>;
          }
          return (
            <div style={{ padding: "5px 0" }}>
              <div>{item.label}</div>
              {item.data && <UserContent user={item.data} />}
            </div>
          );
        }}
      />
    </>
  );
};

export default UserSelector;
